import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, delay, fork, put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import {
  DialogueRegisterState,
  IActionRegister,
  INext,
  IRegisterAction,
  actionsComplete,
  next,
  registerActionResult,
} from 'core/slices/register';
import { actionRequest, attemptRequest, completeRequest, dialogueRequest } from 'core/services/product';
import { IActionAction } from 'types/action/player';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { selectAction } from 'core/selectors/product/action/action';
import { IActionBasic } from 'types/domain/action';
import { selectToken } from 'core/selectors/product/token';
import { IToken, Mode } from 'core/slices/session';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { IActionFeed, IActionFeedItem, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { IPlayerReducer, ViewerState } from 'core/slices/player';
import { SavingState } from 'types/domain/base';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import { CourseNodeRole, ICourseNodeDAO } from 'types/domain/course';

function* handleRegister(action: PayloadAction<IRegisterAction>) {
  if (!action.payload.sessionAttemptId) return;

  const mode: Mode = yield select(selectSessionMode);

  if (mode !== Mode.LEARNING) return;

  const actionRegister: IActionRegister = yield select((state: RootState) =>
    state.register.attempts[action.payload.sessionAttemptId]
      ? state.register.attempts[action.payload.sessionAttemptId].actions[action.payload.actionId]
      : null,
  );

  if (!actionRegister || !action.payload.actionId) return console.warn('No action register');

  if (!action.payload.result) return;

  let sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
  if (sessionAttemptId < 0) {
    const { payload } = yield take(attemptRequest.fulfilled);
    sessionAttemptId = payload.id;
  }

  const register: IActionAction = {
    ...action.payload,
    result: actionRegister.result,
    created: actionRegister.created,
    started: actionRegister.started,
    completed: actionRegister.completed,
    entities: Object.values(actionRegister.entities),
    sessionAttemptId,
  };

  yield put(yield call(actionRequest, register));
}

// How long completing a module waits on its last action's save before going ahead anyway.
const LAST_SAVE_TIMEOUT = 15000;

const selectFeedItemSaving = (state: RootState, item?: IActionFeedItem) =>
  item?.actionId ? state.register.attempts[item.sessionAttemptId]?.actions[item.actionId]?.saving : undefined;

/**
 * The module is completed only once its last action has saved, or the server scores it
 * without that answer. This used to bail out while the save was in flight, which nothing
 * retried: when the save outlasted the answer's feedback delay (a slow API) the auto-next
 * was dropped and the player sat on the last slide. Wait on that action's own register;
 * `loading.action` is shared by every save, and an earlier one landing clears it.
 */
function* waitForLastSave(item: IActionFeedItem) {
  if ((yield select(selectFeedItemSaving, item)) !== SavingState.SAVING) return;

  yield race({ saved: call(untilSaved, item), timedOut: delay(LAST_SAVE_TIMEOUT) });
}

function* untilSaved(item: IActionFeedItem) {
  while ((yield select(selectFeedItemSaving, item)) === SavingState.SAVING)
    yield take([actionRequest.fulfilled, actionRequest.rejected]);
}

function* handleNext(action: PayloadAction<INext>) {
  const mode: Mode = yield select(selectSessionMode);

  if (mode === Mode.LEARNING) {
    const feed: IActionFeed = yield select(selectActionFeed);
    if (feed.feed.length === feed.index + 1) yield waitForLastSave(feed.feed[feed.index]);
  }

  // Re-read after the wait: the learner may have moved on meanwhile.
  const actionFeed: IActionFeed = yield select(selectActionFeed);
  const player: IPlayerReducer = yield select((state: RootState) => state.core.player);
  const sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
  const token: IToken = yield select(selectToken);

  if (
    player.state.viewer.state !== ViewerState.TRANSITIONING &&
    actionFeed.feed.length === actionFeed.index + 1 &&
    action.payload.interaction
  ) {
    if (mode === Mode.LEARNING) {
      if (actionFeed.feed[actionFeed.index].actionId || actionFeed.feed[actionFeed.index].feedback) {
        yield put(
          yield call(completeRequest, {
            sessionAttemptId,
            courseProgressId: token.courseProgressId,
          }),
        );
      } else if (actionFeed.feed[actionFeed.index].courseNodeId) {
        const groupIds = yield select((state: RootState) => Object.keys(state.session.groups));
        const courseNode: ICourseNodeDAO = yield select(state =>
          selectCourseNode(state, actionFeed.feed[actionFeed.index].courseNodeId),
        );
        yield put(
          yield call(dialogueRequest, {
            token: token.token,
            courseNodeId: actionFeed.feed[actionFeed.index].courseNodeId,
            courseProgressId: token.courseProgressId,
            state: DialogueRegisterState.SUBMITTED,
            groupIds: courseNode.entityType === CourseNodeRole.GROUP ? groupIds : [],
            autoNext: true,
          }),
        );
      }

      const nextPage = actionFeed.feed[actionFeed.index + 1];
      if (nextPage?.feedback) {
        yield put(
          yield call(completeRequest, {
            sessionAttemptId,
            courseProgressId: token.courseProgressId,
          }),
        );
      }
    } else {
      yield put(yield call(actionsComplete));
    }
  }
}

function* watchRegister() {
  yield takeEvery(registerActionResult.type, handleRegister);
}

function* watchNext() {
  // Leading: a second next while the first waits on the last save must not complete twice.
  yield takeLeading(next, handleNext);
}

export function* requestSaga() {
  yield all([fork(watchRegister), fork(watchNext)]);
}
